import { createRouter, createWebHistory } from "vue-router";
import filesRoutes from "./files";
import SAdminRoutes from "./SAdminRoutes";
import { hasAccess } from "../utils/access";
import { isLandingPageEnabled } from "../utils/landingPage";
const routes = [
  {
    path: "/",
    name: "Home",
    component: () => import("../views/site/Home.vue"),
  },
  {
    path: "/script",
    name: "AdminSystemConfig",
    component: () => import("../views/admin/Configurations/AdminSystemConfig.vue"),
  },
  {
    path: "/parking",
    name: "VehicleCheck",
    component: () => import("../views/site/VehicleCheck.vue"),
  },
  {
    path: "/documentation",
    name: "Documentation",
    component: () => import("../views/site/Documentation.vue"),
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/admin/Login.vue"),
  },
  {
    path: "/admin/login",
    redirect: "/login",
  },
  {
    path: "/choose-role",
    name: "ChooseRole",
    component: () => import("../views/admin/ChooseRole.vue"),
  },
  {
    path: "/admin/dashboard",
    name: "AdminDashboard",
    component: () => import("../views/admin/Dashboard.vue"),
  },
  {
    path: "/tenant/dashboard",
    name: "TenantDashboard",
    component: () => import("../views/site/TenantDashboard.vue"),
  },
  {
    path: "/tenant/bills",
    name: "TenantBills",
    component: () => import("../views/site/TenantBills.vue"),
  },
  {
    path: "/tenant/subscriptions",
    name: "TenantSubscriptions",
    component: () => import("../views/site/TenantSubscriptions.vue"),
  },
  {
    path: "/tenant/view-subscription/:id",
    name: "ViewSubscription",
    component: () => import("../views/site/ViewSubscription.vue"),
  },
  {
    path: "/tenant/cancellations",
    name: "TenantCancellationRequests",
    component: () => import("../views/site/TenantCancellationRequests.vue"),
  },
  {
    path: "/tenant/messages",
    name: "TenantMessages",
    component: () => import("../views/site/TenantMessages.vue"),
  },
  {
    path: "/tenant/posts",
    name: "TenantPosts",
    component: () => import("../views/site/TenantPosts.vue"),
  },
  {
    path: "/tenant/settings",
    name: "TenantSettings",
    component: () => import("../views/site/TenantSettings.vue"),
  },
  {
    path: "/listing",
    name: "Listing",
    component: () => import("../views/site/Listing.vue"),
  },
  {
    path: "/listing/:id",
    name: "ViewListing",
    component: () => import("../views/site/ViewListing.vue"),
  },
  {
    path: "/admin",
    name: "Admin",
    component: () => import("../views/admin/Home.vue"),
    redirect: "/admin/dashboard",
    children: [...SAdminRoutes, ...filesRoutes],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0, left: 0 };
  },
});

/**
 * Landing: if disabled in system config, never mount Home — redirect to login first.
 * Admin access: redirect to dashboard if admin lacks permission for the route.
 */
router.beforeEach(async (to, from, next) => {
  if (to.path === "/script") {
    next();
    return;
  }

  if (to.name === "Home") {
    const landingOn = await isLandingPageEnabled();
    if (!landingOn) {
      next({ name: "Login", replace: true });
      return;
    }
  }

  if (!to.path.startsWith("/admin") || to.path === "/admin/login") {
    next();
    return;
  }
  const meta = to.meta || {};
  if (meta.sadminOnly) {
    try {
      const raw = localStorage.getItem("ipms_admin");
      if (!raw) {
        next({ path: "/admin/dashboard" });
        return;
      }
      const admin = JSON.parse(raw);
      if (admin.type !== "sadmin") {
        next({ path: "/admin/dashboard" });
        return;
      }
    } catch {
      next({ path: "/admin/dashboard" });
      return;
    }
    next();
    return;
  }
  if (meta.permission) {
    const { module: mod, action, actions } = meta.permission;
    if (mod) {
      if (Array.isArray(actions) && actions.length > 0) {
        // Any-of: route is allowed if the user has any one of these actions on the module.
        // Use this for routes that share a component for both add and edit (e.g. AdminAddTenants).
        const ok = actions.some((a) => hasAccess(mod, a));
        if (!ok) {
          next({ path: "/admin/dashboard" });
          return;
        }
      } else if (action && !hasAccess(mod, action)) {
        next({ path: "/admin/dashboard" });
        return;
      }
    }
  }
  next();
});

export default router;
